import React, { useCallback } from "react";
import EnterpriseGridCommon from "./Ag-Grid";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useTranslation } from "react-i18next";
import ExclamationIcon from "../../assets/exclamation.svg";
import { logDataHelperFunctions } from "../../helpers/logdata-helper";
import { passViewHelper } from "../../helpers/passzone/pass-zone-helper";
import { tlogHelper } from "../../helpers/tlog/tlog-helper";
import {
  getDecimalPoint,
  MEASUREMENT_KEYS,
  GlobalUnitEnum,
} from "../../helpers/unit-conversion";

const TlogGrid = ({
  rows,
  reportColumns,
  isFromPQRTab,
  isM500,
  station_number,
  hideColumnsStationZero,
  hasValidM500Data,
  showRangeColumns,
  getRangeColumn,
  tableLimitData,
  type,
  tlogData,
  loading,
  showRowSelection,
  selectedRowKeys,
  onRowSelected,
  statusCalculationMethod,
  tableHeight,
  hideStatus,
  hideHeaderLimits,
  calculation_level,
  globalUnit = GlobalUnitEnum.IMPERIAL,
}) => {
  const { t } = useTranslation();

  // Field mapping to measurement keys for proper decimal formatting
  const getFieldMeasurementKey = (fieldName) => {
    const fieldMapping = {
      current: MEASUREMENT_KEYS.AMPS,
      volts: MEASUREMENT_KEYS.VOLTS,
      travelSpeed: MEASUREMENT_KEYS.TRAVEL_SPEED,
      wireSpeed: MEASUREMENT_KEYS.WIRE_SPEED,
      oscillationWidth: MEASUREMENT_KEYS.OSCILLATE_WIDTH,
      trueEnergy: MEASUREMENT_KEYS.TRUE_ENERGY,
      heat: MEASUREMENT_KEYS.HEAT,
      frequency: MEASUREMENT_KEYS.FREQUENCY,
      target: MEASUREMENT_KEYS.TARGET,
      horizontalBias: MEASUREMENT_KEYS.HORIZONTAL_BIAS,
      totalWireConsumed: MEASUREMENT_KEYS.TOTAL_WIRE_CONSUMED,
      distance: MEASUREMENT_KEYS.DISTANCE,
      headAngle: MEASUREMENT_KEYS.HEAD_ANGLE,
      arcImpedance: MEASUREMENT_KEYS.ARC_IMPEDANCE,
    };
    return fieldMapping[fieldName];
  };

  const valueRender = (params) => {
    const text = params.value;
    const fieldName = params.colDef?.field;
    const measurementKey = getFieldMeasurementKey(fieldName);
    const decimalPlaces = measurementKey
      ? getDecimalPoint(measurementKey, globalUnit)
      : 2; // default to 2 decimal places

    console.log(
      decimalPlaces,
      fieldName,
      globalUnit,
      "decimalPlaces, fieldName"
    );
    if (text && typeof text === "string" && text.includes("-color")) {
      const numericValue = text.replace("-color", "");
      const parsedValue = parseFloat(numericValue);
      return (
        <div>
          <p className="red-note-text">
            <span className="red-note">
              {isNaN(parsedValue)
                ? numericValue
                : parsedValue.toFixed(decimalPlaces)}
            </span>
          </p>
        </div>
      );
    }
    const parsedValue = parseFloat(text);
    return isNaN(parsedValue) ? "" : parsedValue.toFixed(decimalPlaces);
  };

  function resolveNested(obj, path) {
    return path.split(".").reduce((o, key) => (o ? o[key] : undefined), obj);
  }

  const unitsCommonFilter = {
    pivot: true,
    floatingFilter: true,
    filter: "agMultiColumnFilter",
    filterParams: {
      maxNumConditions: 1,
      filters: [
        {
          filter: "agNumberColumnFilter",
          filterParams: {
            valueFormatter: (params) => {
              return params.value && typeof params.value === "string"
                ? params.value.replace("-color", "")
                : params.value || "";
            },
            maxNumConditions: 1,
          },
        },
      ],
    },
    processCellForExport: (params) => {
      return params.value && typeof params.value === "string"
        ? params.value.replace("-color", "")
        : params.value;
    },
    valueGetter: (params) => {
      const value = resolveNested(params.data, params.colDef.field);
      const numericValue =
        value && typeof value === "string"
          ? value.replace("-color", "")
          : value;
      const parsedValue = parseFloat(numericValue);
      return isNaN(parsedValue) ? numericValue : parsedValue;
    },

    pivotComparator: (a, b) => b.localeCompare(a),
    cellRenderer: (params) => {
      const value = resolveNested(params.data, params.colDef.field);

      return valueRender({
        value: value,
        colDef: params.colDef,
      });
    },
  };

  const columnDefs = useCallback(() => {
    return [
      {
        headerName: t("slogColumntitle.sl.no"),
        field: "sl_no",
        key: "sl_no",

        pivot: true,
        floatingFilter: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          maxNumConditions: 1,
        },
        // headerCheckboxSelection: true,
        // checkboxSelection: true,
        pivotComparator: (a, b) => b.localeCompare(a),
        cellRenderer: (params) => {
          if (
            params.data?.event &&
            selectedRowKeys?.includes(params.data?.event)
          ) {
            params.node.setSelected(true);
          } else {
            params.node.setSelected(false);
          }
          return params.value;
        },
      },
      ...(statusCalculationMethod ==
      passViewHelper.statusCalculationMethod.instantaneous
        ? hideStatus
          ? []
          : [
              {
                headerName: t("slogColumntitle.status"),
                field: "statusNew",
                key: "status",
                pivotComparator: (a, b) => b.localeCompare(a),
                cellRenderer: (params) => {
                  return params.value
                    ? logDataHelperFunctions.getIconForStatus(params.value)
                    : "-";
                },
                processCellForExport: (params) => {
                  return params.value
                    ? logDataHelperFunctions.getExportValueForStatus(
                        params.value
                      )
                    : "-";
                },
                filter: "agMultiColumnFilter",
                filterParams: {
                  filters: [
                    {
                      filter: "agSetColumnFilter",
                      filterParams: {
                        cellHeight: 40,
                        cellRenderer: (params) => {
                          const status = String(params.value);
                          if (status === "(Select All)") {
                            return "(Select All)";
                          }
                          return logDataHelperFunctions.getIconForStatus(
                            status
                          );
                        },
                        valueFormatter: (params) => {
                          return String(params.value);
                        },
                      },
                    },
                  ],
                  maxNumConditions: 1,
                },
              },
            ]
        : []),
      {
        headerName: t("slogColumntitle.event"),
        field: "event",
        key: "event",
        ...unitsCommonFilter,
      },
      {
        headerName: t("slogColumntitle.jobNumber"),
        field: "jobNumber",
        key: "jobNumber",

        pivot: true,
        floatingFilter: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          maxNumConditions: 1,
        },
        pivotComparator: (a, b) => b.localeCompare(a),
      },
      {
        headerName: t("slogColumntitle.time"),
        field: "time",
        key: "time",

        pivot: true,
        floatingFilter: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          maxNumConditions: 1,
        },
        pivotComparator: (a, b) => b.localeCompare(a),
      },
      {
        headerName: t("slogColumntitle.tilt"),
        field: "tilt",
        key: "tilt",

        pivot: true,
        floatingFilter: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          maxNumConditions: 1,
          filters: [
            {
              filter: "agNumberColumnFilter",
              filterParams: {
                maxNumConditions: 1,
              },
            },
          ],
        },
        pivotComparator: (a, b) => b.localeCompare(a),
      },
      {
        headerName: t("slogColumntitle.pipe"),
        field: "pipe",
        key: "pipe",

        pivot: true,
        floatingFilter: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          maxNumConditions: 1,
        },
        pivotComparator: (a, b) => b.localeCompare(a),
      },
      {
        headerName: t("slogColumntitle.pass"),
        field: "pass",
        key: "pass",
        pivotComparator: (a, b) => b.localeCompare(a),
        cellRenderer: (params) => {
          return params.value == "!" ? (
            <img src={ExclamationIcon} alt="Exclamation" />
          ) : (
            params.value
          );
        },
      },
      {
        headerName: t("slogColumntitle.zone"),
        field: "zone",
        key: "zone",

        pivot: true,
        floatingFilter: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          maxNumConditions: 1,
        },
        pivotComparator: (a, b) => b.localeCompare(a),
        cellRenderer: (params) => {
          return params.value == "!" ? (
            <img src={ExclamationIcon} alt="Exclamation" />
          ) : (
            params.value
          );
        },
      },
      {
        headerName: t("slogColumntitle.distance"),
        field: "distance",
        key: "distance",
        ...unitsCommonFilter,
      },
      {
        headerName: `${t("slogColumntitle.travelSpeed")} \n`,
        field: "travelSpeed",
        key: "travelSpeed",
        ...unitsCommonFilter,
        width: 165,
      },
      {
        headerName: `${t("slogColumntitle.volt")}`,
        field: "volts",
        key: "volt",
        ...unitsCommonFilter,
      },
      {
        headerName: `${t("slogColumntitle.current")} \n`,
        field: "current",
        key: "current",
        ...unitsCommonFilter,
      },
      {
        headerName: `${t("slogColumntitle.wireSpeed")}`,
        field: "wireSpeed",
        key: "wireSpeed",
        ...unitsCommonFilter,
        width: 160,
      },
      {
        headerName: t("slogColumntitle.oscillationWidth"),
        field: "oscillationWidth",
        key: "oscillationWidth",
        ...unitsCommonFilter,
      },
      {
        headerName: t("slogColumntitle.target"),
        field: "target",
        key: "target",
        ...unitsCommonFilter,
      },
      {
        headerName: t("slogColumntitle.horizontalBias"),
        field: "horizontalBias",
        key: "horizontalBias",
        ...unitsCommonFilter,
      },
      {
        headerName: t("slogColumntitle.frequency"),
        field: "frequency",
        key: "frequency",
        ...unitsCommonFilter,
      },
      {
        headerName: t("slogColumntitle.totalWireConsumed"),
        field: "totalWireConsumed",
        key: "totalWireConsumed",
        ...unitsCommonFilter,
      },
      {
        headerName: t("slogColumntitle.trueEnergy"),
        field: "trueEnergy",
        key: "trueEnergy",
        ...unitsCommonFilter,
      },
      {
        headerName: `t("slogColumntitle.heat") \n`,
        field: "heat",
        key: "heat",
        ...unitsCommonFilter,
        width: 170,
      },
      {
        headerName: t("slogColumntitle.headAngel"),
        field: "headAngle",
        key: "headAngle",
        ...unitsCommonFilter,
      },
      {
        headerName: t("slogColumntitle.arcImpedance"),
        field: "arcImpedance",
        key: "arcImpedance",
        ...unitsCommonFilter,
      },
    ];
  }, [selectedRowKeys, statusCalculationMethod, hideStatus]);

  let columnsFormatted = columnDefs()?.filter((column) => {
    if (station_number === "0") {
      return !hideColumnsStationZero.includes(column.field);
    }
    return true;
  });

  columnsFormatted = tlogHelper
    .mapTlogColumns(columnsFormatted, reportColumns, isFromPQRTab, isM500)
    ?.filter((ite) =>
      station_number == "0"
        ? hideColumnsStationZero.includes(ite.dataIndex)
          ? null
          : ite
        : ite
    )
    ?.filter((ite) => {
      if (
        !hasValidM500Data &&
        (ite.dataIndex === "headAngle" ||
          ite.dataIndex === "arcImpedance" ||
          ite.dataIndex === "pipe")
      ) {
        return null;
      }
      return ite;
    })
    ?.filter((ite) => ite != null)
    ?.map((column) => {
      return {
        ...column,
        headerName: showRangeColumns?.includes(column.field)
          ? column.headerName +
            " " +
            (!isFromPQRTab
              ? !hideHeaderLimits
                ? getRangeColumn({
                    avgTlogItem: tlogData?.[0],
                    column: tlogHelper.mappingColumnTableLimit[column.field],
                    tableLimitData: tableLimitData,
                    type: type,
                    calculationLevel: calculation_level,
                  })?.showColor
                : ""
              : "")
          : column.headerName,
      };
    });

  const processCellCallback = (params) => {
    if (params?.column?.colDef?.processCellForExport) {
      return params.column.colDef.processCellForExport(params);
    }

    return params.value;
  };

  return (
    <div
      className="ag-theme-alpine tlog-grid-container"
      style={{ width: "100%" }}
    >
      <EnterpriseGridCommon
        isLoading={loading}
        rows={rows}
        columnDefs={columnsFormatted}
        exportFileName={"tlog"}
        processCellCallback={processCellCallback}
        heightValue={tableHeight}
        showRowSelection={showRowSelection}
        onRowSelected={onRowSelected}
      />
    </div>
  );
};

export default TlogGrid;
